/**
 * Chantier : Boilerplate lot 2 — adapter Pennylane (PLAN/audits/audit-boilerplate/BACKLOG.md)
 * Regle metier : INT-PL-01..18 (PRODUIT/domaine/13_integrations.md)
 * Decision : D-V1D-01 (PRODUIT/decisions/) — frontiere d'appel uniquement
 * Adapter unique des invocations Edge Functions pennylane-* (Axe 1 boilerplate).
 * Encapsule le POINT D'APPEL (nom d'EF + body type) et retourne l'outcome brut de
 * supabase.functions.invoke. Aucune gestion d'erreur ici : extractEdgeFunctionBody,
 * detectPennylaneSyncFailure et la collecte syncFailures restent dans les services
 * appelants (contrat D-V1D-01 inchange).
 */
import { supabase } from '@/lib/supabase';

/** Ligne de document PL prete a l'emploi (construite par les services metier). */
export interface PlLigne {
  label: string;
  quantity: number;
  unitPrice: number;
  unit: string;
  type: string;
}

// --- pennylane-quote ---

export function validateQuote(input: {
  devisId: string;
  date: string;
  deadline?: string;
  pdfSubject?: string;
  lignes: PlLigne[];
}) {
  return supabase.functions.invoke('pennylane-quote', {
    body: { action: 'validate', ...input },
  });
}

export function cancelQuote(input: { devisId: string; expectedUpdatedAt: string }) {
  return supabase.functions.invoke('pennylane-quote', {
    body: { action: 'cancel', ...input },
  });
}

export function getQuotePdf(input: { devisId: string }) {
  return supabase.functions.invoke('pennylane-quote', {
    body: { action: 'pdf', ...input },
  });
}

// --- pennylane-invoice ---

export function createInvoice(input: {
  factureId: string;
  date: string;
  deadline: string;
  pdfSubject?: string;
  /** Mois des prestations, en description du PDF (D-FA-LIGNES-DATEES amendee le 2026-11-02). */
  pdfDescription?: string;
  /** Numeros de BDC (texte libre du PDF, D-PL-BDC-ET-MENTION-DEVIS). */
  freeText?: string;
  lignes: PlLigne[];
}) {
  return supabase.functions.invoke('pennylane-invoice', {
    body: { action: 'create', ...input },
  });
}

export function updateInvoiceDraft(input: {
  factureId: string;
  lignes: PlLigne[];
  /** Toujours envoye, chaine vide = effacer les numeros de BDC cote PL. */
  freeText: string;
  /** RC4.05 (D-FA-LIGNES-DATEES) : objet du PDF, renvoye a chaque mise a jour. */
  pdfSubject: string;
  /** Mois des prestations en description du PDF, renvoye a chaque mise a jour ; chaine vide = effacer. */
  pdfDescription: string;
}) {
  return supabase.functions.invoke('pennylane-invoice', {
    body: { action: 'update', ...input },
  });
}

export function finalizeInvoice(input: { factureId: string; deadline: string }) {
  return supabase.functions.invoke('pennylane-invoice', {
    body: { action: 'finalize', ...input },
  });
}

export function deleteInvoice(input: { factureId: string }) {
  return supabase.functions.invoke('pennylane-invoice', {
    body: { action: 'delete', ...input },
  });
}

export function getInvoicePdf(input: { factureId: string }) {
  return supabase.functions.invoke('pennylane-invoice', {
    body: { action: 'pdf', ...input },
  });
}

// --- pennylane-sync ---

export function syncSingleInvoice(input: { factureId: string }) {
  return supabase.functions.invoke('pennylane-sync', {
    body: { source: 'sync_single', ...input },
  });
}

// --- pennylane-customer ---

export function searchCustomers(input: { query: string; mode: 'siret' | 'pl_id' }) {
  return supabase.functions.invoke('pennylane-customer', {
    body: { action: 'search', ...input },
  });
}

export function createCustomer(input: {
  etablissementId: string;
  name: string;
  siret: string;
  email: string;
  address: string;
  postalCode: string;
  city: string;
}) {
  return supabase.functions.invoke('pennylane-customer', {
    body: { action: 'create', ...input },
  });
}

export function linkCustomer(input: { etablissementId: string; plCustomerId: string }) {
  return supabase.functions.invoke('pennylane-customer', {
    body: { action: 'link_existing', ...input },
  });
}

export function unlinkCustomer(input: { etablissementId: string }) {
  return supabase.functions.invoke('pennylane-customer', {
    body: { action: 'unlink', ...input },
  });
}
